import React, { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { BarChart2, CalendarCheck, Clock, FolderKanban, TimerReset } from 'lucide-react'
import { leavesApi } from '../api/leaves'
import { workloadApi } from '../api/workload'
import { Badge } from '../components/common/Badge'
import { Modal } from '../components/common/Modal'
import type { LeaveRequest, LeaveStatus, Project, ProjectValueDashboard, ProjectValueEmployee } from '../types'

const statusColors: Record<LeaveStatus, string> = {
  PENDING: '#f59e0b',
  APPROVED: '#16a34a',
  REJECTED: '#ef4444',
  CANCELLED: '#64748b',
}

const requestTypeColors = {
  LEAVE: '#0f766e',
  TIME_OFF: '#2563eb',
} as const

function requestTypeLabel(leave: LeaveRequest) {
  return leave.requestType === 'TIME_OFF' ? 'Time off' : 'Leave'
}

function durationLabel(leave: LeaveRequest) {
  if (leave.requestType === 'TIME_OFF') {
    const remaining = leave.remainingHours > 0 ? ` · ${formatHours(leave.remainingHours)} pending` : ''
    return `${formatHours(leave.totalHours)}${remaining}`
  }
  return `${leave.totalDays} day${leave.totalDays === 1 ? '' : 's'}`
}

function dateLabel(leave: LeaveRequest) {
  if (leave.requestType === 'TIME_OFF') {
    return `${format(new Date(leave.startDate), 'dd MMM')} · ${leave.startTime || '-'} - ${leave.endTime || '-'}`
  }
  return `${format(new Date(leave.startDate), 'dd MMM')} to ${format(new Date(leave.endDate), 'dd MMM')}`
}

function dateInput(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function addDays(date: Date, amount: number) {
  const next = new Date(date)
  next.setDate(next.getDate() + amount)
  return next
}

function next14Days() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Array.from({ length: 14 }, (_, index) => addDays(today, index))
}

function leaveCoversDay(leave: LeaveRequest, day: Date) {
  const start = new Date(leave.startDate)
  const end = new Date(leave.endDate)
  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  return start <= day && end >= day
}

function statusLabel(status: LeaveStatus) {
  return status.charAt(0) + status.slice(1).toLowerCase()
}

function formatHours(hours: number) {
  if (!Number.isFinite(hours) || Math.abs(hours) < 0.01) return '0h'
  const sign = hours < 0 ? '-' : ''
  const value = Math.abs(hours)
  const wholeHours = Math.floor(value)
  const minutes = Math.round((value - wholeHours) * 60)
  if (minutes === 60) return `${sign}${wholeHours + 1}h`
  return minutes > 0 ? `${sign}${wholeHours}h ${minutes}m` : `${sign}${wholeHours}h`
}

export default function ManagerHomePage() {
  const qc = useQueryClient()
  const [reviewDay, setReviewDay] = useState<Date | null>(null)
  const today = new Date()
  const startDate = dateInput(addDays(today, -29))
  const endDate = dateInput(today)

  const leavesQuery = useQuery({
    queryKey: ['manager-home', 'leaves'],
    queryFn: () => leavesApi.listLeaves().then((res) => res.data.data),
  })

  const projectsQuery = useQuery({
    queryKey: ['manager-home', 'project-values-projects'],
    queryFn: () => workloadApi.listProjectValueProjects().then((res) => res.data.data),
  })

  const leaves = leavesQuery.data ?? []
  const projects = projectsQuery.data ?? []
  const reviewDayLeaves = useMemo(() => (
    reviewDay ? leaves.filter((leave) => leaveCoversDay(leave, reviewDay)) : []
  ), [leaves, reviewDay])

  const invalidateLeaves = () => {
    qc.invalidateQueries({ queryKey: ['manager-home', 'leaves'] })
    qc.invalidateQueries({ queryKey: ['leaves'] })
  }

  const approveMutation = useMutation({
    mutationFn: (leaveId: string) => leavesApi.approveLeave(leaveId),
    onSuccess: invalidateLeaves,
  })

  const rejectMutation = useMutation({
    mutationFn: ({ leaveId, reason }: { leaveId: string; reason?: string }) => leavesApi.rejectLeave(leaveId, reason),
    onSuccess: invalidateLeaves,
  })

  function handleReject(leaveId: string) {
    const reason = window.prompt('Reject reason or note')
    if (reason === null) return
    rejectMutation.mutate({ leaveId, reason })
  }

  const isLeaveActionPending = approveMutation.isPending || rejectMutation.isPending

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div>
          <div style={eyebrowStyle}>Manager Overview</div>
          <h1 style={titleStyle}>Leave Requests and Project Time</h1>
          <p style={subtitleStyle}>Review upcoming leave first, then scan estimate versus actual time for selected projects.</p>
        </div>
      </div>

      <section style={panelStyle}>
        <div style={sectionHeaderStyle}>
          <div style={sectionTitleStyle}>
            <CalendarCheck size={18} />
            <span>Leave Calendar - Next 14 Days</span>
          </div>
        </div>
        {leavesQuery.isLoading ? (
          <div style={emptyStyle}>Loading leave calendar...</div>
        ) : (
          <LeaveCalendar
            leaves={leaves}
            onReviewDay={setReviewDay}
          />
        )}
      </section>

      <section style={panelStyle}>
        <div style={sectionHeaderStyle}>
          <div style={sectionTitleStyle}>
            <FolderKanban size={18} />
            <span>Project Time</span>
          </div>
          <span style={rangeStyle}>Last 30 days</span>
        </div>

        {projectsQuery.isLoading ? (
          <div style={emptyStyle}>Loading projects...</div>
        ) : projects.length === 0 ? (
          <div style={emptyStyle}>No projects are enabled for Project Values.</div>
        ) : (
          <div style={projectListStyle}>
            {projects.map((project) => (
              <ProjectTimeCard key={project.id} project={project} startDate={startDate} endDate={endDate} />
            ))}
          </div>
        )}
      </section>
      <style>{`
        @media (max-width: 1180px) {
          .manager-home-calendar { grid-template-columns: repeat(4, minmax(112px, 1fr)) !important; }
          .manager-project-row { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 760px) {
          .manager-home-calendar { grid-template-columns: repeat(2, minmax(112px, 1fr)) !important; }
        }
      `}</style>
      <Modal
        isOpen={!!reviewDay}
        onClose={() => setReviewDay(null)}
        title={reviewDay ? `Leave Review - ${format(reviewDay, 'EEE dd MMM')}` : 'Leave Review'}
        width={720}
      >
        <div style={{ display: 'grid', gap: 8 }}>
          {reviewDayLeaves.length === 0 && <div style={emptyStyle}>No leave requests on this date.</div>}
          {reviewDayLeaves.map((leave) => (
            <div key={leave.id} style={reviewRowStyle}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13 }}>{leave.employee.fullName}</strong>
                  <Badge color={statusColors[leave.status]} label={statusLabel(leave.status)} size="sm" />
                </div>
                <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>
                  {dateLabel(leave)} · {durationLabel(leave)} · {requestTypeLabel(leave)} · {leave.leaveType || '-'}
                </div>
                {leave.requestType === 'TIME_OFF' && (
                  <div style={{ color: '#2563eb', fontSize: 12, marginTop: 4, fontWeight: 700 }}>
                    Compensation: {formatHours(leave.compensatedHours)} done, {formatHours(leave.remainingHours)} pending
                  </div>
                )}
                {leave.reason && (
                  <div style={{ color: '#334155', fontSize: 12, marginTop: 5, lineHeight: 1.35 }}>
                    {leave.reason}
                  </div>
                )}
              </div>
              {leave.status === 'PENDING' ? (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button disabled={isLeaveActionPending} style={approveButtonStyle} onClick={() => approveMutation.mutate(leave.id)}>Approve</button>
                  <button disabled={isLeaveActionPending} style={rejectButtonStyle} onClick={() => handleReject(leave.id)}>Reject</button>
                </div>
              ) : (
                <span style={{ color: '#64748b', fontSize: 12, fontWeight: 700 }}>Already reviewed</span>
              )}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  )
}

function LeaveCalendar({
  leaves,
  onReviewDay,
}: {
  leaves: LeaveRequest[]
  onReviewDay: (day: Date) => void
}) {
  const days = next14Days()

  return (
    <div className="manager-home-calendar" style={calendarGridStyle}>
      {days.map((day) => {
        const dayLeaves = leaves.filter((leave) => leaveCoversDay(leave, day))
        const pendingCount = dayLeaves.filter((leave) => leave.status === 'PENDING').length
        return (
          <div key={day.toISOString()} style={calendarDayStyle}>
            <button type="button" onClick={() => onReviewDay(day)} style={calendarDateButtonStyle}>
              <span>{format(day, 'EEE dd')}</span>
              {pendingCount > 0 && <span style={pendingPillStyle}>{pendingCount}</span>}
            </button>
            <div style={{ display: 'grid', gap: 6 }}>
              {dayLeaves.length === 0 && <span style={emptyDayStyle}>-</span>}
              {dayLeaves.map((leave) => (
                <button
                  type="button"
                  key={`${day.toISOString()}-${leave.id}`}
                  onClick={() => onReviewDay(day)}
                  style={{ ...leaveChipStyle, borderColor: requestTypeColors[leave.requestType], borderLeftColor: requestTypeColors[leave.requestType] }}
                >
                  <div style={leaveChipTopStyle}>
                    <strong style={leaveNameStyle}>{leave.employee.fullName}</strong>
                    <Badge color={statusColors[leave.status]} label={statusLabel(leave.status)} size="sm" />
                  </div>
                  <div style={{ color: requestTypeColors[leave.requestType], fontSize: 10, fontWeight: 900 }}>
                    {requestTypeLabel(leave)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function ProjectTimeCard({
  project,
  startDate,
  endDate,
}: {
  project: Pick<Project, 'id' | 'name' | 'identifier' | 'color' | 'description' | 'showOnDashboard'>
  startDate: string
  endDate: string
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['manager-home', 'project-value-dashboard', project.id, startDate, endDate],
    queryFn: () => workloadApi.getProjectValueDashboard(project.id, { startDate, endDate }),
  })

  const dashboard: ProjectValueDashboard | undefined = data?.data.data
  const variance = dashboard?.totals.varianceHours ?? 0
  const varianceColor = variance > 0 ? '#dc2626' : '#16a34a'

  return (
    <article className="manager-project-row" style={projectCardStyle}>
      <div style={projectHeaderStyle}>
        <span style={{ ...projectDotStyle, background: project.color }} />
        <div style={{ minWidth: 0 }}>
          <h2 style={projectTitleStyle}>{project.name}</h2>
          <span style={projectKeyStyle}>{project.identifier}</span>
        </div>
      </div>

      {isLoading || !dashboard ? (
        <div style={cardLoadingStyle}>Loading time...</div>
      ) : (
        <>
          <div style={metricGridStyle}>
            <Metric icon={<TimerReset size={14} />} label="Estimated" value={formatHours(dashboard.totals.estimatedHours)} />
            <Metric icon={<Clock size={14} />} label="Actual" value={formatHours(dashboard.totals.actualHours)} />
            <Metric icon={<BarChart2 size={14} />} label="Variance" value={`${variance > 0 ? '+' : ''}${formatHours(variance)}`} color={varianceColor} />
          </div>
          <div style={projectMetaStyle}>
            <span>{dashboard.totals.taskCount} tasks</span>
            <span>{dashboard.totals.employeeCount} employees</span>
          </div>
          <EmployeeEstimateGraph employees={dashboard.employees} />
        </>
      )}
    </article>
  )
}

function Metric({ icon, label, value, color = '#0f172a' }: { icon: React.ReactNode; label: string; value: string; color?: string }) {
  return (
    <div style={metricStyle}>
      <span style={metricIconStyle}>{icon}</span>
      <span style={metricLabelStyle}>{label}</span>
      <strong style={{ ...metricValueStyle, color }}>{value}</strong>
    </div>
  )
}

function EmployeeEstimateGraph({ employees }: { employees: ProjectValueEmployee[] }) {
  const visible = employees.slice(0, 10)
  const maxHours = Math.max(...visible.flatMap((employee) => [employee.estimatedHours, employee.actualHours]), 1)

  return (
    <div style={employeeGraphWrapStyle}>
      <div style={employeeGraphHeaderStyle}>
        <span>Employee Estimated vs Actual</span>
        <span style={employeeLegendStyle}>
          <span style={{ ...legendDotStyle, background: '#0f766e' }} /> Estimated
          <span style={{ ...legendDotStyle, background: '#5eead4', marginLeft: 10 }} /> Actual
        </span>
      </div>
      <div style={employeeGraphGridStyle}>
        {visible.map((employee) => {
          const estimatedHeight = Math.max(6, (employee.estimatedHours / maxHours) * 54)
          const actualHeight = Math.max(6, (employee.actualHours / maxHours) * 54)
          return (
            <div key={employee.user.id} style={employeeBarItemStyle} title={`${employee.user.fullName}: estimated ${formatHours(employee.estimatedHours)}, actual ${formatHours(employee.actualHours)}`}>
              <div style={barPairStyle}>
                <span style={{ ...verticalBarStyle, height: estimatedHeight, background: '#e2e8f0' }} />
                <span style={{ ...verticalBarStyle, height: actualHeight, background: '#5eead4' }} />
              </div>
              <strong style={employeeNameStyle}>{employee.user.fullName}</strong>
              <span style={employeeHoursStyle}>{formatHours(employee.actualHours)}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const pageStyle: React.CSSProperties = {
  padding: 24,
  color: '#0f172a',
}

const headerStyle: React.CSSProperties = {
  marginBottom: 20,
}

const eyebrowStyle: React.CSSProperties = {
  color: '#0f766e',
  fontSize: 12,
  fontWeight: 800,
  textTransform: 'uppercase',
  marginBottom: 8,
}

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 28,
  letterSpacing: 0,
}

const subtitleStyle: React.CSSProperties = {
  margin: '8px 0 0',
  color: '#64748b',
  fontSize: 14,
}

const panelStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #dbe4ef',
  borderRadius: 8,
  padding: 12,
  marginBottom: 12,
}

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 12,
}

const sectionTitleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 16,
  fontWeight: 800,
}

const rangeStyle: React.CSSProperties = {
  color: '#64748b',
  fontSize: 12,
  fontWeight: 700,
}

const calendarGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, minmax(112px, 1fr))',
  gap: 8,
}

const calendarDayStyle: React.CSSProperties = {
  minHeight: 110,
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#f8fafc',
  padding: 7,
}

const calendarDateButtonStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  background: 'transparent',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 6,
  color: '#475569',
  fontSize: 11,
  fontWeight: 800,
  textTransform: 'uppercase',
  marginBottom: 8,
  cursor: 'pointer',
}

const pendingPillStyle: React.CSSProperties = {
  minWidth: 18,
  height: 18,
  borderRadius: 999,
  background: '#fef3c7',
  color: '#b45309',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  fontWeight: 900,
}

const emptyDayStyle: React.CSSProperties = {
  color: '#cbd5e1',
  fontSize: 12,
}

const leaveChipStyle: React.CSSProperties = {
  display: 'grid',
  gap: 5,
  border: '1px solid',
  borderLeft: '4px solid',
  background: '#ffffff',
  borderRadius: 5,
  padding: '4px 6px',
  minWidth: 0,
  textAlign: 'left',
  cursor: 'pointer',
}

const leaveChipTopStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 6,
  minWidth: 0,
}

const leaveNameStyle: React.CSSProperties = {
  fontSize: 12,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const approveButtonStyle: React.CSSProperties = {
  height: 22,
  border: '1px solid #bbf7d0',
  borderRadius: 4,
  background: '#f0fdf4',
  color: '#15803d',
  fontSize: 10,
  fontWeight: 800,
  cursor: 'pointer',
}

const rejectButtonStyle: React.CSSProperties = {
  ...approveButtonStyle,
  borderColor: '#fecaca',
  background: '#fff7f7',
  color: '#dc2626',
}

const projectListStyle: React.CSSProperties = {
  display: 'grid',
  gap: 6,
}

const projectCardStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(220px, 1.4fr) minmax(340px, 2fr) minmax(130px, auto)',
  alignItems: 'center',
  gap: 12,
  border: '1px solid #e2e8f0',
  borderRadius: 7,
  padding: '9px 12px',
  background: '#ffffff',
}

const projectHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  minWidth: 0,
}

const projectDotStyle: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  flexShrink: 0,
}

const projectTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 15,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const projectKeyStyle: React.CSSProperties = {
  color: '#64748b',
  fontSize: 11,
  fontFamily: 'monospace',
}

const metricGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 6,
}

const metricStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '16px 1fr',
  columnGap: 5,
  minWidth: 0,
  padding: '6px 7px',
  borderRadius: 6,
  background: '#f8fafc',
}

const metricIconStyle: React.CSSProperties = {
  color: '#0f766e',
  height: 16,
}

const metricLabelStyle: React.CSSProperties = {
  color: '#64748b',
  fontSize: 10,
  fontWeight: 800,
  textTransform: 'uppercase',
  lineHeight: 1,
}

const metricValueStyle: React.CSSProperties = {
  gridColumn: '2',
  fontSize: 13,
  lineHeight: 1.15,
}

const projectMetaStyle: React.CSSProperties = {
  display: 'grid',
  gap: 8,
  color: '#64748b',
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: 'nowrap',
}

const employeeGraphWrapStyle: React.CSSProperties = {
  gridColumn: '1 / -1',
  borderTop: '1px solid #e2e8f0',
  paddingTop: 10,
}

const employeeGraphHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  alignItems: 'center',
  marginBottom: 8,
  fontSize: 12,
  fontWeight: 800,
  color: '#0f172a',
}

const employeeLegendStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  color: '#64748b',
  fontSize: 11,
  fontWeight: 700,
}

const legendDotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  display: 'inline-block',
  marginRight: 4,
}

const employeeGraphGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(86px, 1fr))',
  gap: 10,
  alignItems: 'end',
}

const employeeBarItemStyle: React.CSSProperties = {
  minWidth: 0,
  display: 'grid',
  justifyItems: 'center',
  gap: 4,
}

const barPairStyle: React.CSSProperties = {
  height: 58,
  display: 'flex',
  gap: 5,
  alignItems: 'end',
}

const verticalBarStyle: React.CSSProperties = {
  width: 16,
  borderRadius: '5px 5px 2px 2px',
  display: 'block',
}

const employeeNameStyle: React.CSSProperties = {
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 11,
  color: '#0f172a',
}

const employeeHoursStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#0f766e',
  fontWeight: 800,
}

const reviewRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: 12,
  alignItems: 'center',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  padding: 10,
  background: '#ffffff',
}

const cardLoadingStyle: React.CSSProperties = {
  color: '#64748b',
  fontSize: 13,
  padding: 12,
  background: '#f8fafc',
  borderRadius: 8,
}

const emptyStyle: React.CSSProperties = {
  color: '#64748b',
  fontSize: 13,
  padding: 16,
  background: '#f8fafc',
  border: '1px dashed #cbd5e1',
  borderRadius: 8,
}
